import { useState } from 'react';

import type { IIRevResultState } from '../../../interface/state/IRev';
import Lightbox from '../child-agent/Lightbox';
import TabMessage from '../child-agent/tabs/TabMessage';
import ResultVideoCard from '../result-video/ResultVideoCard';
import { DocIcon } from './components';

/**
 * Result-sheet image gallery. Thumbnails open the shared focus-trapped lightbox. For a polling-unit
 * result the result video sits under the sheet — this is where an agent adds one after the
 * success screen is gone.
 */
const ResultSheetTab = ({
	result,
	referenceId,
	withVideo = false,
}: {
	result: IIRevResultState;
	referenceId?: string;
	withVideo?: boolean;
}) => {
	const [lightbox, setLightbox] = useState<number | null>(null);
	const images = (result.documentUrls ?? []).filter(Boolean);

	const video = withVideo ? (
		<ResultVideoCard
			context='result'
			electionId={result.election?.id}
			pollingUnitCode={result.pollingUnit?.code}
			pollingUnitName={result.pollingUnit?.name}
			resultReferenceId={referenceId}
			partyScores={(result.results ?? []).map((r) => ({ name: r.partyName, votes: Number(r.votes) || 0 }))}
		/>
	) : null;

	if (!images.length) {
		return (
			<div className='f-column-20'>
				<TabMessage
					icon={<DocIcon />}
					tone='neutral'
					title='No result sheet uploaded'
					hint='No result-sheet image is attached to this submission.'
				/>
				{video}
			</div>
		);
	}

	return (
		<div className='ca-result f-column-20'>
			<div className='ca-result__sheets'>
				<span className='ca-result__section-title'>Result sheet{images.length > 1 ? 's' : ''}</span>
				<div className='ca-result__thumbs'>
					{images.map((url, i) => (
						<button
							type='button'
							className='ca-result__thumb'
							key={url}
							onClick={() => setLightbox(i)}
							aria-label={`Open result sheet ${i + 1}`}
						>
							<img
								src={url}
								alt={`Result sheet ${i + 1}`}
								loading='lazy'
							/>
							<span className='ca-result__thumb-zoom'>View</span>
						</button>
					))}
				</div>
			</div>

			{video}

			{lightbox !== null ? (
				<Lightbox
					images={images}
					startIndex={lightbox}
					label='Result sheet'
					onClose={() => setLightbox(null)}
				/>
			) : null}
		</div>
	);
};

export default ResultSheetTab;
